<template>
    <div class="RiskProfileCriterion">
        <JsonSchemaCustomForm
            v-slot="{ fields }"
            v-model="controller.value"
            :controller="controller"
        >
            <CriterionSettingsRow
                v-if="!filterOutGeneralAggregateLimit && conditionalValueReferences"
                class="py-2.5 flex gap-4 w-full items-center"
            >
                <RbacDisableGuard
                    v-slot="{ isDisabledByRbac }"
                    :features="[RbacFeature.decisioningManageCriteria]"
                >
                    <JsonSchemaCustomFormElement
                        class="flex-1"
                        :field="fields[displayReferenceKey]"
                        :custom-component-input="getCustomComponent"
                        :rbac-disabled="isDisabledByRbac"
                        required
                        show-error
                        :show-validation-errors="showValidationErrors"
                        v-bind="{ disallowedValues: disallowedValues[displayReferenceKey] }"
                        @input="onInput($event, displayReferenceKey)"
                    />
                </RbacDisableGuard>
                <RbacDisableGuard
                    v-if="hasMatchingCustomProperty"
                    v-slot="{ isDisabledByRbac }"
                    :features="[RbacFeature.decisioningManageCriteria]"
                >
                    <JsonSchemaCustomFormElement
                        class="flex flex-col justify-center"
                        custom-title="Set To Field"
                        :field="fields[conditionalValueReferences.flagValueReference]"
                        :custom-component-input="getCustomComponent"
                        :rbac-disabled="isDisabledByRbac"
                        v-bind="{ disallowedValues: disallowedValues[conditionalValueReferences.flagValueReference] }"
                        @input="onInput($event, conditionalValueReferences.flagValueReference)"
                    />
                </RbacDisableGuard>
            </CriterionSettingsRow>

            <CriterionSettingsRow
                v-for="(field, key) in conditionallyFilteredFields
                    .reduce((acc, key) => ({ ...acc, [key]: fields[key] }), {})"
                :key="key"
                class="py-2.5"
                :key-name="key"
                :schema="field.form.schema"
            >
                <RbacDisableGuard
                    v-slot="{ isDisabledByRbac }"
                    :features="[RbacFeature.decisioningManageCriteria]"
                >
                    <JsonSchemaCustomFormElement
                        :field="field"
                        :custom-component-input="getCustomComponent"
                        :rbac-disabled="isDisabledByRbac"
                        :show-validation-errors="showValidationErrors"
                        required
                        show-error
                        v-bind="{ disallowedValues: disallowedValues[key] }"
                        @input="onInput($event, key)"
                    >
                        <template #deleteIcon>
                            <EidIcon
                                :icon-src="faXmark"
                                alt="delete"
                            />
                        </template>
                    </JsonSchemaCustomFormElement>
                </RbacDisableGuard>
            </CriterionSettingsRow>
        </JsonSchemaCustomForm>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import flatten from 'lodash/flatten';
    import isPlainObject from 'lodash/isPlainObject';
    import omit from 'lodash/omit';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import {
        JsonSchemaCustomForm,
        JsonSchemaCustomFormElement,
    } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import RbacDisableGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableGuard.vue';
    import createForm from '@evidentid/json-schema/createForm';
    import { FormController } from '@evidentid/json-schema/FormController';
    import { JsonFormObject } from '@evidentid/json-schema/interfaces/JsonForm';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        ConditionalValueReference,
    } from '@evidentid/tprm-portal-lib/models/decisioning/ConditionalValueReference.model';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import { isSpecialConditionSetsCriterion } from '@/modules/decisioning-criteria/components/CriterionInformationBanner/utils';
    import CriterionSettingsRow from
    '@/modules/decisioning-criteria/components/CriterionSettingsRow/CriterionSettingsRow.vue';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import { buildCriterionSchema } from '@/modules/decisioning-criteria/schemas/buildCriterionSchema';
    import {
        requirementTypeFieldReferenceDelimiter,
        GeneralAggregateLimitReferences,
    } from '@/modules/decisioning-criteria/types';
    import { filterCustomPropertiesBySchema } from '@/modules/decisioning-criteria/utils/filterCustomPropertiesBySchema';
    import { getCriterionJsonSchemaComponent } from '@/modules/decisioning-criteria/utils/getCriterionJsonSchemaComponent';
    import { getFlagDisabledReferenceValueSchema } from '@/modules/decisioning-criteria/utils/getFlagDisabledReferenceValueSchema';
    import { standardizeCriterionSchema } from '@/modules/decisioning-criteria/utils/standardizeCriterionSchema';

    function gerOrderedTemplateReferences(template: CriterionTemplate): string[] {
        return Object.keys(template.evaluator.references) || [];
    }

    /** Reference names whose current value already points at a custom property.
     *
     * Their picker must survive even when no property matches the criterion's value type -- a property
     * that was retyped or is otherwise no longer offered would otherwise take the binding out of the form
     * silently, and the next save would replace it with an empty static value.
     */
    function boundCustomPropertyReferences(values: Record<string, any>): Set<string> {
        return new Set(
            Object.keys(values || {}).filter((key) => values[key]?.isBasedOnInsuredField === true),
        );
    }

    function buildController(
        criterion: EditableRequirementCriterion,
        template: CriterionTemplate,
        customProperties: CustomProperty[],
        // Defaults to the criterion's saved references, which is correct for the initial build.
        // A rebuild triggered by customProperties arriving later must instead pass the form's
        // current value, or it would discard whatever the operator has already entered.
        initValue: Record<string, any> = { ...criterion.references },
    ): FormController {
        const orderedTemplateReferences = gerOrderedTemplateReferences(template);
        const criterionValueSchema = getFlagDisabledReferenceValueSchema({ template });
        return new FormController(
            createForm(
                standardizeCriterionSchema(
                    buildCriterionSchema(criterion.references, orderedTemplateReferences, template),
                    customProperties,
                    criterionValueSchema,
                    boundCustomPropertyReferences(initValue),
                ),
            ),
            initValue,
        );
    }

    /** The parts of the custom properties a criterion's schema is actually built from: the key becomes each
     * picker option's value, the name its label, and the schema decides which pickers offer the property at
     * all. Anything else about a property can change without affecting a single rendered control.
     */
    function customPropertyOptionsSignature(customProperties: CustomProperty[]): string {
        return JSON.stringify((customProperties || []).map(({ key, name, schema }) => [ key, name, schema ]));
    }

    type LoadCriterionMessage = (
        controller: FormController,
        updatedCriterion: EditableRequirementCriterion,
    ) => void;

    export default defineComponent({
        name: 'RiskProfileCriterion',
        components: {
            CriterionSettingsRow,
            EidIcon,
            JsonSchemaCustomForm,
            JsonSchemaCustomFormElement,
            RbacDisableGuard,
        },
        props: {
            criterion: {
                type: Object as PropType<EditableRequirementCriterion>,
                required: true,
            },
            template: {
                type: Object as PropType<CriterionTemplate>,
                required: true,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [] as CustomProperty[],
            },
            loadCriterionMessage: {
                type: Function as PropType<LoadCriterionMessage>,
                default: () => undefined,
            },
            showValidationErrors: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'controller-value-update', 'input', 'reference-accessed' ],
        data() {
            const controller = buildController(this.criterion, this.template, this.customProperties);
            return {
                getCustomComponent: getCriterionJsonSchemaComponent(),
                controller,
                disallowedValues: {} as Record<string, any>,
                // Per reference, per branch, the values that branch last held. Keyed by branch
                // because a property name is not unique across branches -- `insuredField` exists
                // under more than one Limit source, with a different filter schema each time. See
                // restorePrunedBranchValues.
                lastSetBranchValues: {} as Record<string, Record<string, Record<string, any>>>,
                // Per reference, the branch it was last seen on, so a switch can be told apart
                // from an edit made within the same branch.
                lastBranchByReference: {} as Record<string, string | undefined>,
                RbacFeature,
                faXmark,
            };
        },
        computed: {
            conditionalValueReferences(): ConditionalValueReference | null {
                return this.template.displayMetadata.conditionalValueReference || null;
            },
            // A client needs at least one custom property matching this criterion's value type
            // for the "Set To Field" checkbox to appear; otherwise turning it on would offer a
            // dropdown with nothing in it.
            hasMatchingCustomProperty(): boolean {
                if (!this.conditionalValueReferences) {
                    return false;
                }
                const criterionValueSchema = getFlagDisabledReferenceValueSchema({ template: this.template });
                return filterCustomPropertiesBySchema(this.customProperties, criterionValueSchema).length > 0;
            },
            // Set to field reference key
            setToFieldReferenceKey(): string | null {
                const conditionalValueReference = this.conditionalValueReferences;
                if (!conditionalValueReference) {
                    return null;
                }
                return conditionalValueReference.flagValueReference;
            },
            // Custom property reference key
            customPropertyReferenceKey(): string | null {
                const conditionalValueReference = this.conditionalValueReferences;
                if (!conditionalValueReference) {
                    return null;
                }
                return conditionalValueReference.flagEnabledReference;
            },
            // User value reference key
            valueReferenceKey(): string | null {
                const conditionalValueReference = this.conditionalValueReferences;
                if (!conditionalValueReference) {
                    return null;
                }
                return conditionalValueReference.flagDisabledReference;
            },
            displayReferenceKey(): string | null {
                if (!this.conditionalValueReferences) {
                    return null;
                }
                return this.controller.value[this.conditionalValueReferences.flagValueReference]
                    ? this.conditionalValueReferences.flagEnabledReference
                    : this.conditionalValueReferences.flagDisabledReference;
            },
            orderedTemplateReferences(): string[] {
                return gerOrderedTemplateReferences(this.template);
            },
            unavailableFieldReference(): any {
                const conditionalValueReference = this.conditionalValueReferences;
                if (!conditionalValueReference) {
                    return this.orderedTemplateReferences;
                }
                const flagValue = conditionalValueReference
                    ? this.criterion.references[conditionalValueReference.flagValueReference]
                    : false;
                const unavailableFieldReference = flagValue
                    ? conditionalValueReference.flagDisabledReference
                    : conditionalValueReference.flagEnabledReference;
                return unavailableFieldReference;
            },
            conditionallyFilteredFields(): string[] {
                const conditionalValueReference = this.conditionalValueReferences;
                if (!conditionalValueReference) {
                    return this.orderedTemplateReferences;
                }
                const conditionalReferenceKeys = Object.values(conditionalValueReference);
                return this.orderedTemplateReferences.filter((reference) => (
                    !conditionalReferenceKeys.includes(reference)
                ));
            },
            filterOutGeneralAggregateLimit(): boolean {
                return Boolean(this.isSpecialConditionSetsCriterion &&
                    this.criterion
                        .references[GeneralAggregateLimitReferences.generalAggregateLimitThreshold] !== undefined &&
                    this.controller?.value.conditionSets?.length > 0);
            },
            isSpecialConditionSetsCriterion(): boolean {
                return isSpecialConditionSetsCriterion(this.criterion);
            },
        },
        watch: {
            // An insured-field picker gets its enum options from customProperties (see
            // standardizeCriterionSchema), and the controller built in data() never revisits its
            // schema -- so a criterion that renders before the properties arrive, or that outlives
            // an edit to them, is left showing options that no longer match reality. Waiting only
            // for the empty-to-non-empty transition covers the first case and not the second: a
            // property added, renamed, retyped, or removed while the form is open would go
            // unnoticed until a full page reload.
            //
            // Comparing what the options are actually built from, rather than the array's identity,
            // is what keeps this from rebuilding on every unrelated re-render -- a parent that hands
            // down a fresh array of the same properties changes nothing here. Seeding the rebuilt
            // controller from criterion.references (as the initial build does) would discard
            // whatever the operator has already typed, so the form's current value is carried
            // forward instead.
            customProperties(next: CustomProperty[], previous: CustomProperty[]): void {
                if (customPropertyOptionsSignature(next) === customPropertyOptionsSignature(previous)) {
                    return;
                }
                this.controller = buildController(
                    this.criterion,
                    this.template,
                    next,
                    { ...this.controller.value },
                );
            },
        },
        created() {
            // Seed the remembered branch values from what the criterion was opened with, so the very
            // first switch away from a source can still restore it. Without this, only values the
            // operator retyped during this session would survive a round trip.
            Object.keys(this.criterion.references).forEach((key) => {
                this.restorePrunedBranchValues(this.controller.value[key], key);
            });
            this.loadCriterionMessage?.(this.controller as FormController, this.criterion);
            this.$emit('controller-value-update', this.controller.value);
        },
        methods: {
            isKeyConditionSets(key: string) {
                return key === GeneralAggregateLimitReferences.conditionSets;
            },
            onInput(inputValue: any, key: string): void {
                const value = this.restorePrunedBranchValues(inputValue, key);
                if (value !== inputValue) {
                    this.controller.value[key] = value;
                }
                this.trackInputValues(value, key);
                const fieldForm = this.referenceForm(key);
                const isValid = fieldForm?.isValid(fieldForm?.getValue(value, true), true) || false;
                const controllerValues = this.controller.form.getValue(this.controller.value);
                const updateCriterion: EditableRequirementCriterion = {
                    ...this.criterion,
                    references: Object.keys(this.criterion.references).reduce((acc, refKey) => {
                        acc[refKey] = refKey === key ? value : controllerValues[refKey];
                        return acc;
                    }, {} as Record<string, any>),
                };
                const reference = [
                    this.criterion.coverageType,
                    this.criterion.field,
                    key,
                ].join(requirementTypeFieldReferenceDelimiter);
                this.$emit('input', updateCriterion);
                this.$emit('reference-accessed', { reference, isValid });
                this.$emit('controller-value-update', this.controller.value);
                this.loadCriterionMessage?.(this.controller as FormController, updateCriterion);
            },
            /**
             * The property whose value selects which branch of a conditional reference is active.
             *
             * Read from the schema rather than assumed, so this stays usable by any reference built
             * the same way: the Limit source nests `if`/`then`/`else` three deep and every `if` tests
             * the same single property. A reference whose `if` clauses disagree, or that has none,
             * returns null and is left alone -- better than guessing at a discriminator and moving
             * values between branches on the strength of it.
             */
            branchProperty(key: string): string | null {
                const names = new Set<string>();
                const walk = (schema: any): void => {
                    if (!schema || typeof schema !== 'object') {
                        return;
                    }
                    const required = schema.if?.required;
                    if (Array.isArray(required) && required.length === 1) {
                        names.add(required[0]);
                    }
                    walk(schema.then);
                    walk(schema.else);
                };
                walk(this.template.evaluator.references[key]?.schema);
                return names.size === 1 ? [ ...names ][0] : null;
            },
            /**
             * Give a reference back the values it had on this branch the last time it was here.
             *
             * A conditional reference keeps each branch's own inputs under `then`/`else`, and
             * FormController only ever holds the active branch's properties -- so switching the Limit
             * criterion's source discards the amount that was typed for Specified Limit, and switching
             * back yields null rather than what was there. PRODUCT-26397 requires the opposite: the
             * value is preserved, so returning to a source shows what the operator last set on it.
             *
             * Restoring happens ONLY when the branch just changed. Within a branch a null property is
             * the operator clearing the field, and putting the old amount back there would make the
             * limit impossible to empty -- and would hide the very state the row-level revalidation
             * exists to catch.
             *
             * What a branch holds is recorded under that branch, replacing whatever was there rather
             * than merging into it, so a deliberate clear is remembered as cleared and does not
             * reappear on the next round trip.
             */
            restorePrunedBranchValues(value: any, key: string): any {
                const fieldForm = this.referenceForm(key);
                if (!fieldForm || !isPlainObject(value)) {
                    return value;
                }
                const discriminator = this.branchProperty(key);
                if (!discriminator) {
                    return value;
                }
                const branch = String(value[discriminator] ?? '');
                const byBranch = this.lastSetBranchValues[key] || {};
                const previousBranch = this.lastBranchByReference[key];
                const switchedBranch = previousBranch !== undefined && previousBranch !== branch;

                // Returned as-is unless something is actually restored, so callers can compare
                // identity to decide whether the controller needs reassigning.
                let result = value;
                if (switchedBranch) {
                    const remembered = byBranch[branch] || {};
                    const candidate = { ...value };
                    let changed = false;
                    (fieldForm as JsonFormObject).getProperties?.(candidate)?.forEach(({ name }) => {
                        if (name === discriminator) {
                            return;
                        }
                        // Take the arriving branch's own value, or nothing. Not just filling the
                        // blanks: a property name can be declared by more than one branch -- both
                        // entity sources offer `insuredField`, against different filter schemas --
                        // and FormController has no reason to prune a name the new branch also
                        // declares, so the field the operator chose for the previous source would
                        // otherwise ride across the switch into a picker that does not offer it.
                        const next = remembered[name] ?? null;
                        if (candidate[name] !== next) {
                            candidate[name] = next;
                            changed = true;
                        }
                    });
                    if (changed) {
                        result = candidate;
                    }
                }

                const held: Record<string, any> = {};
                (fieldForm as JsonFormObject).getProperties?.(result)?.forEach(({ name }) => {
                    if (name !== discriminator && result[name] != null) {
                        held[name] = result[name];
                    }
                });
                this.lastSetBranchValues = { ...this.lastSetBranchValues, [key]: { ...byBranch, [branch]: held } };
                this.lastBranchByReference = { ...this.lastBranchByReference, [key]: branch };

                return result;
            },
            referenceForm(key: string) {
                return (this.controller?.form as JsonFormObject).getProperties()
                    .find((property) => property.name === key)?.form;
            },
            trackInputValues(value: any, key: string): void {
                // tracking siblings with same type of input values, for fields
                // does not allow duplicate value across form
                if (this.isSpecialConditionSetsCriterion && this.isKeyConditionSets(key)) {
                    const allAppliesPer = flatten(value?.map((
                        conditionSetsVal: any) => conditionSetsVal.generalAggregateLimitAppliesPer,
                    )).filter(Boolean);
                    if (allAppliesPer?.length > 0) {
                        this.disallowedValues = {
                            ...this.disallowedValues,
                            conditionSets: { generalAggregateLimitAppliesPer: allAppliesPer },
                        };
                    } else if (this.disallowedValues?.conditionSets?.generalAggregateLimitAppliesPer) {
                        this.disallowedValues = {
                            ...this.disallowedValues,
                            conditionSets: omit(this.disallowedValues.conditionSets, 'generalAggregateLimitAppliesPer'),
                        };
                    }
                }
            },
        },
    });
</script>
